import React, { useEffect } from 'react';
import { Col, Row, Form, Card } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { IoCloseOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
// images
import DefaultImage from 'assets/img/preview.jpg';
// components
import Button from 'components/backoffice/buttonCustom';
import CustomInput from 'components/backoffice/customInput';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import SearchProduct from 'components/backoffice/customInput/SearchProduct';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';

const AwardWinningBookForm = ({
    isEditing,
    onSubmitForm,
    onDelete,
    openModalDelete,
    setOpenModalDelete,
    defaultValues,
    loading,
}) => {
    const { t } = useTranslation(['backoffice']);
    const {
        control,
        handleSubmit,
        setValue,
        reset,
        setError,
        clearErrors,
        formState: { errors },
        watch,
    } = useForm();

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'products',
    });

    const watchedFields = watch();

    const handleOnDragEnd = (result) => {
        if (!result.destination) return;
        const items = [...watchedFields.products];
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setValue('products', items);
    };

    const onAddProduct = (product) => {
        if (product !== null && !fields.some((p) => p.value === product.value)) {
            append(product);
            clearErrors('products');
        }
    };

    useEffect(() => {
        if (!isEditing) reset(defaultValues);
    }, [reset, defaultValues, isEditing]);

    useEffect(() => {
        if (defaultValues) {
            setValue('nombre', defaultValues.nombre);
            if (isEditing && defaultValues.products && defaultValues.products.length > 0) {
                const firstProduct = defaultValues.products[0];
                if (firstProduct.year) {
                    setValue('year', firstProduct.year);
                }
            }
            if (defaultValues.products) {
                const customProducts = defaultValues.products.map((prod) => ({
                    id: prod.id,
                    ean: prod.ean,
                    title: prod.title,
                    image: prod.cover,
                    label: `${prod.title} (${prod.ean})`,
                    value: prod.id,
                }));
                setValue('products', customProducts);
            }
        }
    }, [defaultValues, setValue, isEditing]);

    const onSubmit = (data) => {
        if (!fields || fields.length === 0) {
            setError('products', {
                type: 'manual',
                message: t('atLeastOneProductRequired'),
            });
            return;
        }
        onSubmitForm(data);
    };

    return (
        <div className="mt-5">
            <Form onSubmit={handleSubmit(onSubmit)}>
                <Row className="mt-4">
                    <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
                        <Controller
                            name="nombre"
                            control={control}
                            rules={{
                                required: { value: true, message: t('camporequerido') },
                            }}
                            render={({ field }) => (
                                <CustomInput
                                    value={field.value}
                                    onChange={(e) => field.onChange(e)}
                                    type="text"
                                    label={t('nameAward')}
                                    placeholder={t('name')}
                                />
                            )}
                        />
                        {errors?.nombre && <small className="text-danger">{errors.nombre.message}</small>}
                    </Col>
                    <Col sm={12} md={12} lg={6} className="mb-md-2 mb-lg-0">
                        <Controller
                            name="year"
                            control={control}
                            rules={{
                                required: { value: true, message: t('camporequerido') },
                                pattern: {
                                    value: /^\d{4}$/,
                                    message: t('yearExactLength'),
                                },
                                validate: (value) => {
                                    const yearNum = parseInt(value, 10);
                                    if (isNaN(yearNum) || yearNum <= 0) {
                                        return t('yearMustBePositive');
                                    }
                                    return true;
                                },
                            }}
                            render={({ field }) => (
                                <CustomInput
                                    value={field.value || ''}
                                    onChange={(value) => {
                                        const numericValue = value.replace(/\D/g, '');
                                        field.onChange(numericValue);
                                    }}
                                    type="text"
                                    label={t('year')}
                                    placeholder={t('year')}
                                    maxLength={4}
                                />
                            )}
                        />
                        {errors?.year && <small className="text-danger">{errors.year.message}</small>}
                    </Col>
                </Row>
                <Row className="mt-4">
                    <Col sm={12} md={12} lg={12} className="mb-md-2 mb-lg-0">
                        <Form.Label className='text-light'>{t('seleccionarproducto')}</Form.Label>
                        <SearchProduct
                            filterBy="search"
                            onSelected={onAddProduct}
                            labelShow="ean"
                            placeholder={t('searchByEanTitle')}
                        />
                        <DragDropContext onDragEnd={handleOnDragEnd}>
                            <Droppable droppableId="droppable-products" direction="vertical">
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef} className="mt-4">
                                        {fields.map((product, index) => (
                                            <Draggable key={product.id} draggableId={product.id.toString()} index={index}>
                                                {(provided, snapshot) => (
                                                    <Card
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="mb-3"
                                                        style={{
                                                            background: snapshot.isDragging ? '#f1f1f1' : 'white',
                                                            ...provided.draggableProps.style,
                                                        }}
                                                    >
                                                        <Card.Body className="d-flex justify-content-between align-items-center">
                                                            <div className="d-flex align-items-center">
                                                                <img
                                                                    src={product.image || DefaultImage}
                                                                    alt="cover"
                                                                    width="70"
                                                                    height="70"
                                                                    className="me-3"
                                                                />
                                                                <div>
                                                                    <h6>{product.title}</h6>
                                                                </div>
                                                            </div>
                                                            <IoCloseOutline size="24" onClick={() => remove(index)} />
                                                        </Card.Body>
                                                    </Card>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                        {errors?.products && (
                            <small className="text-danger mt-2 d-block">{errors.products.message}</small>
                        )}
                    </Col>
                </Row>
            </Form>
            <div className="my-4 d-flex justify-content-end gap-3">
                <Button color="primary" onClick={handleSubmit(onSubmit)} loading={loading}>
                    {t('save')}
                    <SpinnerComponent loading={loading} />
                </Button>
                {isEditing && (
                    <Button color="secondary" onClick={() => setOpenModalDelete(true)} loading={loading}>
                        {t('eliminar')}
                    </Button>
                )}
            </div>
            <DeleteModalCustom
                openModal={openModalDelete}
                setOpenModal={setOpenModalDelete}
                handleDelete={onDelete}
                deleteTitle={t('deleteAwardWinningBook')}
                beforeTitle={t('awardWinningBooks')}
            />
        </div>
    );
};

AwardWinningBookForm.propTypes = {
    onSubmitForm: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    openModalDelete: PropTypes.bool,
    setOpenModalDelete: PropTypes.func,
    defaultValues: PropTypes.object,
    isEditing: PropTypes.bool.isRequired,
    loading: PropTypes.bool.isRequired,
};

export default AwardWinningBookForm;